// Ana api.js'den apiRequest fonksiyonunu import et
import { apiRequest } from './api';
import * as XLSX from 'xlsx';

/**
 * Shared query params for sales call list, filter-options, and dashboard (exclude page, ordering).
 * Hotel filter matches backend `hotel__hotel_name__icontains`.
 */
function appendSalesCallFilterQueryParams(queryParams, filters) {
  if (!filters) return;
  if (filters.start_date) queryParams.append('start_date__gte', filters.start_date);
  if (filters.end_date) queryParams.append('end_date__lte', filters.end_date);
  if (filters.agency) queryParams.append('agency__icontains', filters.agency);
  if (filters.agency_contact) queryParams.append('agency_contact__icontains', filters.agency_contact);
  if (filters.phone) queryParams.append('phone__icontains', filters.phone);
  if (filters.type) queryParams.append('type', filters.type);
  if (filters.communication_type) queryParams.append('communication_type', filters.communication_type);
  if (filters.hotel) queryParams.append('hotel__hotel_name__icontains', filters.hotel);
  if (filters.country) queryParams.append('country__icontains', filters.country);
  if (filters.city) queryParams.append('city__icontains', filters.city);
  if (filters.sales_representative) queryParams.append('sales_representative__icontains', filters.sales_representative);
  if (filters.call_status) queryParams.append('call_status', filters.call_status);
  if (filters.call_result) queryParams.append('call_result', filters.call_result);
  if (filters.created_at__gte) queryParams.append('created_at__gte', filters.created_at__gte);
  if (filters.created_at__lte) queryParams.append('created_at__lte', filters.created_at__lte);
}

// Sales API fonksiyonları
export const SABLON_2_FILENAME = 'sablon_2.xlsx';

export const salesApiService = {
  // Hotels
  getHotels: async () => {
    return await apiRequest('/sales/hotels/');
  },
  getHotel: async (id) => {
    return await apiRequest(`/sales/hotels/${id}/`);
  },

  // Room Types
  getRoomTypes: async () => {
    return await apiRequest('/sales/roomtypes/');
  },
  getRoomType: async (id) => {
    return await apiRequest(`/sales/roomtypes/${id}/`);
  },

  // Open Stop Sales
  getOpenStopSales: async () => {
    return await apiRequest('/sales/openstopsales/');
  },
  getOpenStopSale: async (id) => {
    return await apiRequest(`/sales/openstopsales/${id}/`);
  },
  createOpenStopSale: async (data) => {
    return await apiRequest('/sales/openstopsales/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  /** Birden fazla kayıt; backend tek özet mail gönderir (kullanıcı başına). */
  createOpenStopSalesBulk: async (items) => {
    return await apiRequest('/sales/openstopsales/bulk-create/', {
      method: 'POST',
      body: JSON.stringify({ items }),
    });
  },
  updateOpenStopSale: async (id, data) => {
    return await apiRequest(`/sales/openstopsales/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  deleteOpenStopSale: async (id) => {
    return await apiRequest(`/sales/openstopsales/${id}/`, {
      method: 'DELETE',
    });
  },
  getOpenStopSalesByHotel: async (hotelId) => {
    return await apiRequest(`/sales/openstopsales/by_hotel/?hotel_id=${hotelId}`);
  },
  getStopSaleMailRecipients: async () => {
    return await apiRequest('/sales/openstopsales/mail-recipients/');
  },
  updateStopSaleMailRecipients: async (userIds) => {
    return await apiRequest('/sales/openstopsales/mail-recipients/', {
      method: 'PUT',
      body: JSON.stringify({ user_ids: userIds }),
    });
  },

  // Date Types
  getDateTypes: async () => {
    return await apiRequest('/sales/datetypes/');
  },

  // Markets
  getMarkets: async () => {
    return await apiRequest('/sales/markets/');
  },

  // Operators
  getOperators: async () => {
    return await apiRequest('/sales/operators/');
  },

  // Open Stop Types
  getOpenStopTypes: async () => {
    return await apiRequest('/sales/openstoptypes/');
  },

  // Male Only
  getMaleOnly: async () => {
    return await apiRequest('/sales/maleonly/');
  },

  // Agencies
  getAgencies: async () => {
    return await apiRequest('/sales/agencies/');
  },

  // Sales Calls
  getSalesCallFilterOptions: async (filters = {}) => {
    let url = '/sales-calls/calls/filter-options/';
    const queryParams = new URLSearchParams();
    appendSalesCallFilterQueryParams(queryParams, filters);
    const queryString = queryParams.toString();
    if (queryString) url += `?${queryString}`;
    return await apiRequest(url);
  },

  getSalesCalls: async (filters = {}) => {
    let url = '/sales-calls/calls/';
    const queryParams = new URLSearchParams();
    const { page, ordering, ...rest } = filters;
    appendSalesCallFilterQueryParams(queryParams, rest);
    if (ordering) queryParams.append('ordering', ordering);
    if (page) queryParams.append('page', page);

    const queryString = queryParams.toString();
    if (queryString) url += `?${queryString}`;

    return await apiRequest(url);
  },

  getSalesDashboardStats: async (filters = {}) => {
    let url = '/sales-calls/calls/dashboard-stats/';
    const queryParams = new URLSearchParams();
    appendSalesCallFilterQueryParams(queryParams, filters);

    const queryString = queryParams.toString();
    if (queryString) url += `?${queryString}`;

    return await apiRequest(url);
  },

  getSalesCall: async (id) => {
    return await apiRequest(`/sales-calls/calls/${id}/`);
  },

  createSalesCall: async (data) => {
    return await apiRequest('/sales-calls/calls/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateSalesCall: async (id, data) => {
    return await apiRequest(`/sales-calls/calls/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteSalesCall: async (id) => {
    return await apiRequest(`/sales-calls/calls/${id}/`, {
      method: 'DELETE',
    });
  },

  /** Who receives automatic PDF when a new Sales Call is created (GET/PATCH). */
  getSalesCallAutoNotifySettings: async () => {
    return await apiRequest('/sales-calls/calls/auto-notify-settings/');
  },

  patchSalesCallAutoNotifySettings: async (recipientUserId) => {
    return await apiRequest('/sales-calls/calls/auto-notify-settings/', {
      method: 'PATCH',
      body: JSON.stringify({ recipient_user_id: recipientUserId }),
    });
  },

  // Sales Call daily mail schedules (recipient + send time records)
  getSalesCallMailSchedules: async () => {
    return await apiRequest('/sales-calls/mail-schedules/');
  },

  createSalesCallMailSchedule: async (data) => {
    return await apiRequest('/sales-calls/mail-schedules/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateSalesCallMailSchedule: async (id, data) => {
    return await apiRequest(`/sales-calls/mail-schedules/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteSalesCallMailSchedule: async (id) => {
    return await apiRequest(`/sales-calls/mail-schedules/${id}/`, {
      method: 'DELETE',
    });
  },

  /** Queue email with PDF attachment (MailQueue + email_daemon / Mandrill). */
  emailSalesCallPdf: async (id, data = {}) => {
    return await apiRequest(`/sales-calls/calls/${id}/email-pdf/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** One email with a single PDF containing all listed sales call IDs. */
  emailSalesCallPdfBatch: async (data) => {
    return await apiRequest('/sales-calls/calls/email-pdf-batch/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  uploadSalesCallAttachments: async (id, formData) => {
    return await apiRequest(`/sales-calls/calls/${id}/attachments/`, {
      method: 'POST',
      body: formData,
    });
  },

  deleteSalesCallAttachment: async (attachmentId) => {
    return await apiRequest(`/sales-calls/attachments/${attachmentId}/`, {
      method: 'DELETE',
    });
  },

  importSalesCalls: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return await apiRequest('/sales-calls/import/', {
      method: 'POST',
      body: formData,
    });
  },

  downloadSalesCallTemplate: () => {
    const headers = [
      'call_id',
      'guest_id',
      'start_date',
      'end_date',
      'agency',
      'agency_contact',
      'phone',
      'sales_representative',
      'type',
      'country',
      'pax',
      'city',
      'communication_type',
      'call_status',
      'call_result',
      'hotel_id',
      'address',
      'summary',
    ];
    const sampleRows = [
      [
        '',
        '12345',
        '2026-01-15 10:00',
        '2026-01-15 11:30',
        'Example Agency',
        'John Doe',
        '+90 555 123 4567',
        'Ahmet Yilmaz',
        'Sales Call',
        'Turkey',
        '2',
        'Istanbul',
        'Yüz Yüze',
        'Tamamlandı',
        'Başarılı',
        '',
        'Sample address',
        'Acente ile gorusme yapildi',
      ],
      [
        '',
        '',
        '2026-02-20 14:00',
        '2026-02-20 15:00',
        'Demo Travel',
        'Jane Smith',
        '+90 555 987 6543',
        'Ayse Demir',
        'Workshop',
        'Germany',
        '5',
        'Berlin',
        'Telefon',
        'Planlandı',
        'Beklemede',
        '',
        '',
        'Telefon gorusmesi planlandi',
      ],
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sales Calls');
    XLSX.writeFile(wb, 'sales_call_import_template.xlsx');
  },

  downloadSablon2Template: () => {
    const link = document.createElement('a');
    link.href = `${process.env.PUBLIC_URL || ''}/templates/sablon_2.xlsx`;
    link.download = SABLON_2_FILENAME;
    link.click();
  },

  // Travel Expenses
  getTravelExpenses: async () => {
    return await apiRequest('/sales-calls/expenses/');
  },
  createTravelExpense: async (data) => {
    return await apiRequest('/sales-calls/expenses/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },
  updateTravelExpense: async (id, data) => {
    return await apiRequest(`/sales-calls/expenses/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },
  deleteTravelExpense: async (id) => {
    return await apiRequest(`/sales-calls/expenses/${id}/`, { method: 'DELETE' });
  },

  // Representatives
  getRepresentatives: async () => {
    return await apiRequest('/sales-calls/representatives/');
  },

  // Marketing Budget Data for B2B Dashboard
  getMarketingMarkets: async () => {
    return await apiRequest('/marketing-budget/markets/');
  },

  getMarketingCountries: async (marketId) => {
    let url = '/marketing-budget/countries/';
    if (marketId) url += `?market=${marketId}`;
    return await apiRequest(url);
  },

  createMarketingCountry: async (data) => {
    return await apiRequest('/marketing-budget/countries/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getMarketingCities: async (countryId) => {
    let url = '/marketing-budget/cities/';
    if (countryId) url += `?country=${countryId}`;
    return await apiRequest(url);
  },

  createMarketingCity: async (data) => {
    return await apiRequest('/marketing-budget/cities/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },


  getMarketingPivotSalesCall: async (year) => {
    let url = '/marketing-budget/pivot-salescall/';
    if (year) url += `?budget_year=${year}`;
    return await apiRequest(url);
  },

  // Visit Reports (Sales Call)
  getVisitReports: async (filters = {}) => {
    let url = '/marketing-budget/visit-reports/';
    const queryParams = new URLSearchParams();
    if (filters.visit_year) queryParams.append('visit_year', filters.visit_year);
    if (filters.market) queryParams.append('market', filters.market);
    if (filters.country) queryParams.append('country', filters.country);
    if (filters.city != null && filters.city !== '') queryParams.append('city', filters.city);
    if (filters.visit_month != null && filters.visit_month !== '') queryParams.append('visit_month', filters.visit_month);

    if (queryParams.toString()) url += `?${queryParams.toString()}`;
    return await apiRequest(url);
  },

  createVisitReport: async (data) => {
    return await apiRequest('/marketing-budget/visit-reports/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateVisitReport: async (id, data) => {
    return await apiRequest(`/marketing-budget/visit-reports/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // B2B Contacts
  getB2BContacts: async () => {
    return await apiRequest('/actions/b2bcontacts/');
  },
  getB2BContact: async (id) => {
    return await apiRequest(`/actions/b2bcontacts/${id}/`);
  },
  createB2BContact: async (data) => {
    return await apiRequest('/actions/b2bcontacts/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  updateB2BContact: async (id, data) => {
    return await apiRequest(`/actions/b2bcontacts/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  deleteB2BContact: async (id) => {
    return await apiRequest(`/actions/b2bcontacts/${id}/`, {
      method: 'DELETE',
    });
  }
};


export default salesApiService;
